import { CSSProperties } from 'react'

import Big from 'big.js'
import { isLeft } from 'fp-ts/lib/Either'

import { useAsset } from 'contexts/Assets'
import {
  FormatterOption,
  QFManagerContextState,
  useQuantityFormatting,
} from 'contexts/QuantityFormatting'
import useConversion from 'hooks/useConversion'
import { useShouldColorCodeQuantityConversions } from 'hooks/useShouldColorCodeQuantityConversions'
import { Asset, AssetId } from 'types/Asset'
import { Override } from 'types/utils'

import {
  FormatterFn,
  FormatterFnOpts,
  allBuiltinFormatters,
  defaultFormatterFnOpts,
  getFormatter,
} from './formatters'
import {
  getAssetOrFallback,
  getBigOrFallback,
  getNeedsConversion,
} from './helpers'

export type QuantityProps = {
  /** The quantity being displayed */
  quantity: number | string | Big | null | undefined
  /** The currency that `quantity` is in */
  currency: AssetId | undefined
  /**
   * A function that takes in the current Quantity Formatting context, and
   * returns a new context, including a formatter. Can be used for advanced
   * manipulation of which formatter/currency conversion to use, given the
   * global settings.
   */
  override?: MapQuantityCtxFn
  /** Options that will be passed to formatter functions */
  formatterOpts?: Partial<FormatterFnOpts>
}

/**
 * How we represent the QuantityManager context internally to the Quantity
 * component. Includes the addition of a `formatter` field, which should either
 * be a straight mapping from the `selectedFormatter` to one of the built-in
 * `FormatterOptions`, or a custom formatter (in which case the
 * `selectedFormatter` field should be set to `Custom`)
 */
type QuantityCtx = Override<
  QFManagerContextState,
  { selectedFormatter: FormatterOption | 'Custom'; formatter: FormatterFn }
>

export type MapQuantityCtxFn = (
  ctx: QFManagerContextState,
) => QFManagerContextState | QuantityCtx

export const useQuantityContext = (mapper?: MapQuantityCtxFn): QuantityCtx => {
  const qfCtx = useQuantityFormatting()
  const ctx = mapper ? mapper(qfCtx) : qfCtx
  if ('formatter' in ctx) {
    if (!allBuiltinFormatters.includes(ctx.formatter)) {
      return { ...ctx, selectedFormatter: 'Custom' }
    } else {
      return ctx
    }
  } else {
    return { ...ctx, formatter: getFormatter(ctx.selectedFormatter) }
  }
}

export const Quantity: React.FC<QuantityProps> = ({
  quantity: quantityRaw,
  currency: currencyRaw,
  override,
  formatterOpts: formatterOptsRaw,
}) => {
  const currencyAsset = useAsset(currencyRaw)
  const { selectedCurrency, formatter, selectedFormatter } =
    useQuantityContext(override)
  const shouldColorCodeQuantityConversions =
    useShouldColorCodeQuantityConversions()
  const formatterOpts = { ...defaultFormatterFnOpts, ...formatterOptsRaw }

  const quantityOrFallback = getBigOrFallback(quantityRaw)
  if (isLeft(quantityOrFallback)) {
    return <>{quantityOrFallback.left}</>
  }
  const quantity = quantityOrFallback.right

  const assetOrFallback = getAssetOrFallback(currencyAsset)
  if (isLeft(assetOrFallback)) {
    if (selectedCurrency === 'Native') {
      return <>{formatter(quantity, undefined, formatterOpts)}</>
    }
    return <>{assetOrFallback.left}</>
  }
  const currency = assetOrFallback.right

  const color = shouldColorCodeQuantityConversions
    ? selectedCurrency === 'USD'
      ? selectedFormatter === 'Thousands'
        ? usdThousandsColor
        : selectedFormatter === 'Millions'
          ? usdMillionsColor
          : undefined
      : selectedCurrency === 'BTC'
        ? btcColor
        : undefined
    : undefined

  if (getNeedsConversion(currency, selectedCurrency)) {
    return (
      <ConvertedQuantity
        formatter={formatter}
        quantity={quantity}
        fromCurrency={currency}
        toCurrencyId={selectedCurrency}
        formatterOpts={formatterOpts}
        color={color}
      />
    )
  } else {
    return (
      <NativeQuantity
        formatter={formatter}
        quantity={quantity}
        currency={currency}
        formatterOpts={formatterOpts}
        color={color}
      />
    )
  }
}

type NativeQuantityProps = {
  formatter: FormatterFn
  quantity: Big
  currency: Asset
  formatterOpts: FormatterFnOpts
  color?: CSSProperties['color']
}

export const NativeQuantity: React.FC<NativeQuantityProps> = ({
  formatter,
  quantity,
  currency,
  formatterOpts,
  color,
}) => {
  return (
    <span style={{ color }}>
      {formatter(quantity, currency, formatterOpts)}
    </span>
  )
}

type ConvertedQuantityProps = {
  formatter: FormatterFn
  quantity: Big
  fromCurrency: Asset
  toCurrencyId: AssetId
  formatterOpts: FormatterFnOpts
  color?: CSSProperties['color']
}

export const ConvertedQuantity: React.FC<ConvertedQuantityProps> = ({
  formatter,
  quantity,
  fromCurrency,
  toCurrencyId,
  formatterOpts,
  color,
}) => {
  const toCurrency = useAsset(toCurrencyId)
  const conversion = useConversion(
    fromCurrency,
    toCurrency.loading ? undefined : toCurrency.data,
  )
  if (Number.isNaN(conversion)) {
    return <>NaN</>
  }
  const converted = new Big(quantity).times(conversion)
  const toCurrencyOrFallback = getAssetOrFallback(toCurrency)
  if (isLeft(toCurrencyOrFallback)) {
    return <>{toCurrencyOrFallback.left}</>
  }
  return (
    <span style={{ color }}>
      {formatter(converted, toCurrencyOrFallback.right, formatterOpts)}
    </span>
  )
}

export const usdThousandsColor: CSSProperties['color'] = 'darkviolet'

export const usdMillionsColor: CSSProperties['color'] = 'fuchsia'

export const btcColor: CSSProperties['color'] = '#e08618'
